import {
	useEffect,
	useState,
	useRef,
} from 'react'
import {
	useRecoilState,
	useRecoilValue,
	useSetRecoilState,
} from 'recoil'
import axios, { type AxiosResponse } from 'axios'
import mempoolJS from '@mempool/mempool.js'
import { differenceInHours, format } from 'date-fns'
import { useSearchParams, useLocation } from 'react-router-dom'
import { usePrevious } from '@uidotdev/usehooks'

import {
	useAppSelector,
	useAppDispatch,
	useIsScrolling,
} from '../../hooks'
import { setFullNewBlockRipple } from '../features/settings/settingsReducer'
import {
	calculateMagicNumber,
	type CircleBreakpointsStateKey,
	filterArrayBySecondArrayProperty,
	getHashrateAverages,
	getKeyByValue,
} from '../../utils'
import {
	connectionState,
	isConnectedState,
	appInitStartTimeState,
	blockState,
	scrubBlockState,
	// blockTxsState,
	showBlockDropState,
	showBlockRippleState,
	currentDifficultyState,
	prevDiffDateState,
	estimatedRetargetDateState,
	difficultyChangeState,
	remainingBlocksState,
	previousRetargetState,
	previousTimeState,
	timeAvgState,
	economyFeeState,
	fastestFeeState,
	hashrateAveragesState,
	maxMempoolState,
	mempoolMinFeeState,
	mempoolSizeState,
	mempoolUsageState,
	mempoolBlocksState,
	txVBytesPerSecondState,
	pastBlocksState,
	confModeState,
	confImgState,
	confImgTopState,
	confImgWidthState,
	confUpperImgState,
	confBgImgState,
	confUpperMessageState,
	confLowerMessageState,
	nodesState,
	chainHeightState,
	totalAdressesState,
	totalUtxosState,
	totalVbState,
	totalDiskState,
	totalTxnsCountState,
	staticModeState,
	landscapeOrientationState,
	circlesBreakpointState,
	circlesWrapWidthState,
	artBlocksState,
	artistsState,
	confBgImgBlendState,
	pageVisibleState,
	networkOnlineState,
	ArtBlock,
} from '../../state'
import {
	MEMPOOL_SPACE_API_V1_URL,
	BITNODES_API_URL,
	TIMECHAININDEX_API_URL,
	CIRCLE_BREAKS,
	TIMECHAIN_CALENDAR_DATA_URL,
	TIMECHAIN_CALENDAR_API_URL,
} from '../../constants'
import { abbreviateMempoolSize } from '../../utils'
import type {
	Block,
	DA,
	Fees,
	MempoolInfo,
} from '../../models'

export const AppData = () => {
	const now = new Date()
	const dispatch = useAppDispatch()
	const { fullNewBlockRipple } = useAppSelector(({ settings }) => settings)
	const [connection, setConnection] = useRecoilState(connectionState)
	const setIsConnected = useSetRecoilState(isConnectedState)
	const [reconnecting, setReconnecting] = useState(false)
	const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null)
	const wsRef = useRef<WebSocket | null>(null)
	const intentionalCloseRef = useRef(false)
	const setPageVisible = useSetRecoilState(pageVisibleState)
	const setNetworkOnline = useSetRecoilState(networkOnlineState)
	const pageVisible = useRecoilValue(pageVisibleState)
	const networkOnline = useRecoilValue(networkOnlineState)
	const [pastBlocks, setPastBlocks] = useRecoilState(pastBlocksState)
	const [block, setBlock] = useRecoilState(blockState)
	const setScrubBlock = useSetRecoilState(scrubBlockState)
	const setNodesCount = useSetRecoilState(nodesState)
	const staticMode = useRecoilValue(staticModeState)
	const [lastHashrateCall, setLastHashrateCall] = useState(now)
	const [hashrateInit, setHashrateInit] = useState(false)
	const [lastChainstateCall, setLastChainstateCall] = useState(now)
	const [chainstateInit, setChainstateInit] = useState(false)
	const setAppInitStartTime = useSetRecoilState(appInitStartTimeState)
	const [landscapeOrientation, setLandscapeOrientation] = useRecoilState(landscapeOrientationState)
	const setArtBlocks = useSetRecoilState(artBlocksState)
	const setArtists = useSetRecoilState(artistsState)
	const [searchParams] = useSearchParams()
	const location = useLocation()
	const isScrolling = useIsScrolling()

	const [retargetDate, setRetargetDate] = useState(0)
	const prevRetargetDate = usePrevious(retargetDate)
	const setEstimatedRetagetDate = useSetRecoilState(estimatedRetargetDateState)

	const [diffChange, setDiffChange] = useState(0)
	const prevDiffChange = usePrevious(diffChange)
	const setDifficultyChange = useSetRecoilState(difficultyChangeState)
	
	const [remBlocks, setRemBlocks] = useState(0)
	const prevRemBlocks = usePrevious(remBlocks)
	const setRemainingBlocks = useSetRecoilState(remainingBlocksState)
	
	const [prevRetarget, setPrevRetarget] = useState(0)
	const prevPrevRetarget = usePrevious(prevRetarget)
	const setPreviousRetarget = useSetRecoilState(previousRetargetState)

	const [prevTime, setPrevTime] = useState(0)
	const prevPrevTime = usePrevious(prevTime)
	const setPreviousTime = useSetRecoilState(previousTimeState)

	const [timeAv, setTimeAv] = useState(0)
	const prevTimeAv = usePrevious(timeAv)
	const setTimeAvg = useSetRecoilState(timeAvgState)

	const [maxMem, setMaxMem] = useState(0)
	const prevMaxMem = usePrevious(maxMem)
	const setMaxMempool = useSetRecoilState(maxMempoolState)

	const [minFee, setMinFee] = useState(0)
	const prevMinFee = usePrevious(minFee)
	const setMempoolMinFee = useSetRecoilState(mempoolMinFeeState)

	const [poolSize, setPoolSize] = useState(0)
	const prevPoolSize = usePrevious(poolSize)
	const setMempoolSize = useSetRecoilState(mempoolSizeState)

	const [poolUsage, setPoolUsage] = useState(0)
	const prevPoolUsage = usePrevious(poolUsage)
	const setMempoolUsage = useSetRecoilState(mempoolUsageState)

	const [poolBlocks, setPoolBlocks] = useState(0)
	const prevPoolBlocks = usePrevious(poolBlocks)
	const setMempoolBlocks = useSetRecoilState(mempoolBlocksState)

	const [econFee, setEconFee] = useState(0)
	const prevEconFee = usePrevious(econFee)
	const setEconomyFee = useSetRecoilState(economyFeeState)

	const [fastFee, setFastFee] = useState(0)
	const prevFastFee = usePrevious(fastFee)
	const setFastestFee = useSetRecoilState(fastestFeeState)

	const setHashrateAverages = useSetRecoilState(hashrateAveragesState)
	
	const [inflow, setInflow] = useState(0)
	const prevInflow = usePrevious(inflow)
	const setTxVBytesPerSecond = useSetRecoilState(txVBytesPerSecondState)

	const setShowBlockDrop = useSetRecoilState(showBlockDropState)
	const setShowBlockRipple = useSetRecoilState(showBlockRippleState)
	const [currenDiff, setCurrentDifficulty] = useRecoilState(currentDifficultyState)
	const [prevDiffDate, setPrevDiffDate] = useRecoilState(prevDiffDateState)
	const setConfMode = useSetRecoilState(confModeState)
	const setConfImg = useSetRecoilState(confImgState)
	const setConfImgTop = useSetRecoilState(confImgTopState)
	const setConfImgWidth = useSetRecoilState(confImgWidthState)
	const setConfUpperImg = useSetRecoilState(confUpperImgState)
	const setConfBgImg = useSetRecoilState(confBgImgState)
	const setConfBgImgBlend = useSetRecoilState(confBgImgBlendState)
	const setUpperMessage = useSetRecoilState(confUpperMessageState)
	const setLowerMessage = useSetRecoilState(confLowerMessageState)
	const setChainHeight = useSetRecoilState(chainHeightState)
	const setTotalAdresses = useSetRecoilState(totalAdressesState)
	const setTotalUtxos = useSetRecoilState(totalUtxosState)
	const setTotalVb = useSetRecoilState(totalVbState)
	const setTotalDisk = useSetRecoilState(totalDiskState)
	const setTotalTxnsCount = useSetRecoilState(totalTxnsCountState)

	const [circlesBreakpoint, setCirclesBreakpoint] = useRecoilState(circlesBreakpointState)
	const [circlesWrapWidth, setCirclesWrapWidth] = useRecoilState(circlesWrapWidthState)

	// const setBlockTxs = useSetRecoilState(blockTxsState)
	// const selfHosted = process.env.REACT_APP_SELF_HOSTED === 'true'

	type Blk = {
		block_index: number
		hash: string
		height: number
		time: number
	}

	const handleGetPastBlocks = () => {
		const last24hBlocksRequest = `${TIMECHAIN_CALENDAR_DATA_URL}/v1/last24hblocks`
		const MAX_ATTEMPTS = 3
		const TIMEOUT = 7000

		const fetchBlocksWithRetry = async (attempts = 1): Promise<AxiosResponse<Blk[]>> => {
			const tryMessage = attempts === MAX_ATTEMPTS
				? '[24h blocks] fetch, last attempt'
				: `[24h blocks] fetch, attempt ${attempts}`
		
			try {
				console.info(tryMessage)
				const response = await Promise.race([
					axios.get<Blk[]>(last24hBlocksRequest),
					new Promise((_, reject) =>
						setTimeout(() => reject(new Error('[24h blocks] timeout')), TIMEOUT)
					),
				]) as AxiosResponse<Blk[]>
				
				if (response.data.length === 0 && attempts < MAX_ATTEMPTS) {
					console.info('[24h blocks] Empty response, retrying')
					return new Promise((resolve) => {
						setTimeout(() => {
							resolve(fetchBlocksWithRetry(attempts + 1))
						}, TIMEOUT)
					})
				}
				return response
			} catch (error) {
				if (attempts < MAX_ATTEMPTS) {
					console.info(`[24h blocks] fetch attempt ${attempts} failed.`)
					console.info('[24h blocks] retry')
					return new Promise((resolve) => {
						setTimeout(() => {
							resolve(fetchBlocksWithRetry(attempts + 1))
						}, 5000)
					})
				} else {
					console.error('[24h blocks] All attempts failed')
					throw error
				}
			}
		}

		fetchBlocksWithRetry()
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			.then((response: any) => {
				console.info('[24h blocks] success count:', response.data.data.length)
				// eslint-disable-next-line
				const last24hBlocks = response.data.data
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				const formattedPastBlocks = last24hBlocks.map((block: any) => {
					return {
						height: block.height,
						id: block.hash,
						timestamp: block.time,
					}
				})
				if (formattedPastBlocks.length === 0) {
					console.error('[24h blocks] fetch failed')
				}
				setPastBlocks(formattedPastBlocks)
			})
	}

	const handleGetNodes = () => {
		const RETRY_INTERVAL = 3600000

		console.info('[nodes] fetching')

		const fetchNodes = () => {
			axios.get(BITNODES_API_URL)
				.then((res) => {
					const nodesCount = res.data.results[0].total_nodes
					console.info('[nodes] success count:', nodesCount)
					setNodesCount(nodesCount)
				})
				.catch(error => {
					console.error('[nodes] error:', error)
				})
				.then(() => {
					// Schedule the next call after 1 hour
					setTimeout(fetchNodes, RETRY_INTERVAL)
				})
		}

		fetchNodes()
	}

	const handleGetGallery = () => {
		const requestUrl = `${TIMECHAIN_CALENDAR_API_URL}/gallery-items`

		console.info('[gallery] fetching')

		const fetchGalleryItems = () => {
			axios.get(requestUrl)
				.then((res) => {
					const { art, artists } = res.data
					// @ts-ignore
					const liveArtBlocks = art ? filterArrayBySecondArrayProperty(art, 'a', artists, 'name') as ArtBlock[] : [] as ArtBlock[]
					console.info('[gallery] art/artists:', `${liveArtBlocks.length}/${artists.length}`, `(${art.length})`)
					setArtBlocks(liveArtBlocks)
					setArtists(artists)
				})
				.catch(error => {
					console.error('[gallery] error:', error)
				})
		}

		fetchGalleryItems()
	}

	const handleInitializeBlocks = (blocks: Block[]) => {
		const latestBlock = blocks[blocks.length - 1]
		console.info(`[mempool ws] initialized at block ${latestBlock.height}`)
		setBlock(latestBlock)
		setScrubBlock(latestBlock)
		// handleGetBlockTxs(latestBlock.id)
	}

	const handleSetBlock = (block: Block) => {
		console.info(`[mempool ws] new block ${block.height}`)
		setBlock(block)
		if (!staticMode) {
			setScrubBlock(block)
		}
		setShowBlockDrop(true)
		setShowBlockRipple(true)
		// handleGetBlockTxs(block.id)
	}

	const handleSetDa = (da: DA) => {
		const {
			estimatedRetargetDate,
			difficultyChange,
			previousRetarget,
			previousTime,
			remainingBlocks,
			timeAvg,
		} = da

		setRetargetDate(estimatedRetargetDate)
		setDiffChange(difficultyChange)
		setRemBlocks(remainingBlocks)
		setPrevRetarget(previousRetarget)
		setPrevTime(previousTime)
		setTimeAv(timeAvg)
	}

	const handleSetMempoolInfo = (mempoolInfo: MempoolInfo) => {
		const {
			maxmempool,
			mempoolminfee,
			size,
			usage,
		} = mempoolInfo

		setMaxMem(maxmempool)
		setMinFee(mempoolminfee)
		setPoolSize(size)
		setPoolUsage(usage)
	}

	const formatFee = (fee: number) => {
		if (fee < 1) return parseFloat(fee.toFixed(2))
		if (fee < 5) return parseFloat(fee.toFixed(1))
		return Math.round(fee)
	}

	const handleSetFees = (fees: Fees) => {
		const {
			economyFee,
			fastestFee,
		} = fees

		setEconFee(formatFee(economyFee))
		setFastFee(formatFee(fastestFee))
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleSetMempoolBlocks = (mempoolBlocks: any[]) => {
		let newMempoolBlocks = 1
		if (mempoolBlocks.length > 7) {
			const mpBlocksMinusLast = mempoolBlocks.length - 1
			const mpLastBlock = mempoolBlocks[mempoolBlocks.length - 1]
			const mpLastBlockCount = mpLastBlock.blockVSize / 1000
			newMempoolBlocks = Math.round((mpBlocksMinusLast + (mpLastBlockCount / 1000)))
		} else {
			newMempoolBlocks = mempoolBlocks.length
		}

		setPoolBlocks(newMempoolBlocks)
	}

	// STATE CHANGE EFFECTS
	useEffect(() => {
		if (format(new Date(prevRetargetDate), 'yyyy-MM-dd') !== format(new Date(retargetDate), 'yyyy-MM-dd')) {
			setEstimatedRetagetDate(retargetDate)
		}
	}, [prevRetargetDate, retargetDate])
	useEffect(() => {
		if (prevDiffChange === null || parseFloat(prevDiffChange.toFixed(2)) !== parseFloat(diffChange.toFixed(2))) {
			setDifficultyChange(diffChange)
		}
	}, [prevDiffChange, diffChange])
	useEffect(() => {
		if (prevRemBlocks !== remBlocks) {
			setRemainingBlocks(remBlocks)
		}
	}, [prevRemBlocks, remBlocks])
	useEffect(() => {
		if (prevPrevRetarget !== prevRetarget) {
			setPreviousRetarget(prevRetarget)
		}
	}, [prevPrevRetarget, prevRetarget])
	useEffect(() => {
		if (prevPrevTime !== prevTime) {
			setPreviousTime(prevTime)
		}
	}, [prevPrevTime, prevTime])
	useEffect(() => {
		if (prevTimeAv !== timeAv) {
			setTimeAvg(timeAv)
		}
	}, [prevTimeAv, timeAv])
	useEffect(() => {
		if (prevMaxMem !== maxMem) {
			setMaxMempool(maxMem)
		}
	}, [prevMaxMem, maxMem])
	useEffect(() => {
		if (prevMinFee !== minFee) {
			setMempoolMinFee(minFee)
		}
	}, [prevMinFee, minFee])
	useEffect(() => {
		if (prevPoolSize !== poolSize) {
			setMempoolSize(poolSize)
		}
	}, [prevPoolSize, poolSize])
	useEffect(() => {
		if (abbreviateMempoolSize(prevPoolUsage) !== abbreviateMempoolSize(poolUsage)) {
			setMempoolUsage(poolUsage)
		}
	}, [prevPoolUsage, poolUsage])
	useEffect(() => {
		if (prevPoolBlocks !== poolBlocks) {
			setMempoolBlocks(poolBlocks)
		}
	}, [prevPoolBlocks, poolBlocks])
	useEffect(() => {
		if (prevInflow !== inflow) {
			setTxVBytesPerSecond(inflow)
		}
	}, [prevInflow, inflow])
	useEffect(() => {
		if (prevEconFee !== econFee) {
			setEconomyFee(econFee)
		}
	}, [prevEconFee, econFee])
	useEffect(() => {
		if (prevFastFee !== fastFee) {
			setFastestFee(fastFee)
		}
	}, [prevFastFee, fastFee])

	// DEFAULT COLOR MODE
	useEffect(() => {
		if (!localStorage.getItem('chakra-ui-color-mode-default')) {
			localStorage.setItem('chakra-ui-color-mode', 'dark')
			localStorage.setItem('chakra-ui-color-mode-default', 'set')
		}
	}, [])

	// SET APP INITIALIZATION START TIME
	useEffect(() => {
		const now = new Date()
		setAppInitStartTime(now)
	}, [])

	// INITIAL LANDSCAPE ORIENTATION CALCULATION
	useEffect(() => {
		setLandscapeOrientation(window.innerWidth > window.innerHeight)
	}, [])

	// CALCULATE APP LAYOUT
	useEffect(() => {
		const calculateAppLayout = () => {
			const windowWidth = window.innerWidth
			const windowHeight = window.innerHeight
			const newLandscapeOrientation = windowWidth > windowHeight

			const magicNumber = calculateMagicNumber(windowWidth, windowHeight)
			const breakpoint = getKeyByValue(CIRCLE_BREAKS, magicNumber) as CircleBreakpointsStateKey
			const landString = newLandscapeOrientation ? 'LAND' : 'PORT'

			setCirclesBreakpoint(breakpoint)
			setCirclesWrapWidth(magicNumber)
			console.info('[ui]', landString, breakpoint, magicNumber)
		}

		if (circlesWrapWidth === 0) {
			calculateAppLayout()
		}
	}, [circlesWrapWidth])

	// WINDOW RESIZE TRIGGERS RECALCULATE APP LAYOUT & BREAKPOINT
	useEffect(() => {
		const recalculate = () => {
			if (!isScrolling && circlesWrapWidth !== 0) {
				const w = window.innerWidth
				const h = window.innerHeight
				const newLandscapeOrientation = w > h

				const newMagicNumber = calculateMagicNumber(w, h)
				const newBreakpoint = getKeyByValue(CIRCLE_BREAKS, newMagicNumber) as CircleBreakpointsStateKey
				const newLandString = newLandscapeOrientation ? 'LAND' : 'PORT'

				if (newLandscapeOrientation !== landscapeOrientation) {
					setLandscapeOrientation(newLandscapeOrientation)
				}
				if (newBreakpoint !== circlesBreakpoint) {
					setCirclesBreakpoint(newBreakpoint)
				}
				if (newMagicNumber !== circlesWrapWidth) {
					setCirclesWrapWidth(newMagicNumber)
				}
				if (newLandscapeOrientation !== landscapeOrientation
					|| newBreakpoint !== circlesBreakpoint
					|| newMagicNumber !== circlesWrapWidth
				) {
					console.info('[ui] resize:', newLandString, newBreakpoint, newMagicNumber)
				}
			}
		}

		window.addEventListener('resize', recalculate)
		return () => window.removeEventListener('resize', recalculate)
	}, [
		isScrolling,
		landscapeOrientation,
		circlesWrapWidth,
		circlesBreakpoint,
	])
	
	// DEFAULT FULL BLOCK RIPPLE
	useEffect(() => {
		if (fullNewBlockRipple === undefined) {
			dispatch(setFullNewBlockRipple(true))
		}
	}, [])

	// URL PATHS
	useEffect(() => {
		const { pathname } = location

		if (pathname.includes('conference')) {
			console.info('[pathname] conference')
			setConfMode(true)
		}
	}, [location])

	// URL PARAMS
	useEffect(() => {
		if (searchParams.get('img_url')) {
			const imgUrlString = searchParams.get('img_url')
			const imgUrl = imgUrlString ? decodeURI(imgUrlString) : ''
			if (imgUrl) {
				setConfImg(imgUrl)
			}
		} else {
			setConfImg('')
		}

		if (searchParams.get('img_top')) {
			const imgTop = searchParams.get('img_top')
			if (imgTop) {
				setConfImgTop(Number(imgTop))
			}
		} else {
			setConfImgTop(0)
		}

		if (searchParams.get('img_width')) {
			const imgWidth = searchParams.get('img_width')
			if (imgWidth) {
				setConfImgWidth(Number(imgWidth))
			}
		} else {
			setConfImgWidth(50)
		}

		if (searchParams.get('upper_img_url')) {
			const upperImgUrlString = searchParams.get('upper_img_url')
			const upperImgUrl = upperImgUrlString ? decodeURI(upperImgUrlString) : ''
			if (upperImgUrl) {
				setConfUpperImg(upperImgUrl)
			}
		} else {
			setConfUpperImg('')
		}

		if (searchParams.get('bg_img_url')) {
			const bgImgUrlString = searchParams.get('bg_img_url')
			const bgImgUrl = bgImgUrlString ? decodeURI(bgImgUrlString) : ''
			if (bgImgUrl) {
				setConfBgImg(bgImgUrl)
			}
		} else {
			setConfBgImg('')
		}

		if (searchParams.get('bg_img_blend')) {
			const bgImgBlendString = searchParams.get('bg_img_blend')
			const bgImgBlend = bgImgBlendString ? decodeURI(bgImgBlendString) : ''
			if (bgImgBlend) {
				setConfBgImgBlend(bgImgBlend)
			}
		} else {
			setConfBgImg('')
		}

		if (searchParams.get('upper_message')) {
			const messageString = searchParams.get('upper_message')
			const message = messageString ? decodeURI(messageString) : ''
			if (message) {
				setUpperMessage(message)
			}
		} else {
			setUpperMessage('')
		}

		if (searchParams.get('lower_message')) {
			const messageString = searchParams.get('lower_message')
			const message = messageString ? decodeURI(messageString) : ''
			if (message) {
				setLowerMessage(message)
			}
		} else {
			setLowerMessage('')
		}
	}, [searchParams])

	// 24H BLOCKS INIT
	useEffect(() => {
		handleGetPastBlocks()
	}, [])

	// NODES INIT
	useEffect(() => {
		handleGetNodes()
	}, [])

	// GALLERY INIT
	useEffect(() => {
		handleGetGallery()
	}, [])

	// 24H BLOCKS UPDATE ON NEW BLOCK
	useEffect(() => {
		const newBlockCondition = pastBlocks.length > 0
			&& block.height !== pastBlocks[pastBlocks.length - 1]?.height
		if (newBlockCondition) {
			console.info(`[24h blocks] add new block ${block.height}`)
			const newSimpleBlock = {
				height: block.height,
				id: block.id,
				timestamp: block.timestamp,
			}
			setPastBlocks((prevPastBlocks) => [...prevPastBlocks, newSimpleBlock])
		}
	}, [
		block,
		pastBlocks,
	])

	// HASHRATE
	useEffect(() => {
		const now = new Date()
		// not initialized or more than 1 hour since last time called
		const shouldFetchHashrates = Boolean(!hashrateInit || differenceInHours(now, lastHashrateCall) > 0)

		const handleGetHashrateAndDifficulty = () => {
			console.info('[hashrates] fetch')
			axios.get(`${MEMPOOL_SPACE_API_V1_URL}/mining/hashrate/1y`)
				.then((res) => {
					const {
						currentHashrate,
						currentDifficulty,
						hashrates,
						difficulty,
					} = res.data
					const { length } = hashrates
					const yesterdayHashrate = hashrates[length - 1].avgHashrate
					const hashrateAverages = getHashrateAverages(hashrates, currentHashrate)
					const {
						sevenDayAverage,
						yesterdaySevenDayAverage,
						twoWeekAverage,
						yesterdayTwoWeekAverage,
						monthAverage,
						yesterdayMonthAverage,
						threeMonthAverage,
						yesterdayThreeMonthAverage,
					} = hashrateAverages
					const prevDiff = difficulty[difficulty.length - 1].time

					setHashrateAverages({
						current: currentHashrate,
						yesterday: yesterdayHashrate,
						sevenDay: sevenDayAverage,
						yesterdaySevenDay: yesterdaySevenDayAverage,
						twoWeek: twoWeekAverage,
						yesterdayTwoWeek: yesterdayTwoWeekAverage,
						month: monthAverage,
						yesterdayMonth: yesterdayMonthAverage,
						threeMonth: threeMonthAverage,
						yesterdayThreeMonth: yesterdayThreeMonthAverage,
					})

					if (currenDiff !== currentDifficulty) {
						setCurrentDifficulty(currentDifficulty)
					}

					if (prevDiff !== prevDiffDate) {
						setPrevDiffDate(prevDiff)
					}

					if (!hashrateInit) {
						setHashrateInit(true)
					}

					setLastHashrateCall(now)
				})
		}

		if (shouldFetchHashrates) {
			handleGetHashrateAndDifficulty()
		}
	}, [block])

	// CHAINSTATE
	useEffect(() => {
		// not initialized or more than 18 hours since last time called
		const shouldFetchChainstate = Boolean(!chainstateInit || differenceInHours(now, lastChainstateCall) > 17)

		const handleGetChainstate = () => {
			const API_ENDPOINT = `${TIMECHAININDEX_API_URL}/chainstate/latest`
	
			const fetchChainstate = () => {
				axios.get(API_ENDPOINT)
					.then((res) => {
						const { data } = res
						const {
							height,
							totaladdresses,
							totalutxos,
							totalvb,
							totaldisksize,
							totaltxnscount,
						} = data[0]
	
						console.info('[chainstate] success')
						setChainHeight(height)
						setTotalAdresses(totaladdresses)
						setTotalUtxos(totalutxos)
						setTotalVb(totalvb)
						setTotalDisk(totaldisksize)
						setTotalTxnsCount(totaltxnscount)
					})
					.catch(error => {
						console.error('[chainstate] error:', error)
					})
			}
			
			console.info('[chainstate] fetching')
			fetchChainstate()
			setChainstateInit(true)
			setLastChainstateCall(now)
		}

		if (shouldFetchChainstate) {
			handleGetChainstate()
		}
	}, [block])

	// MEMPOOL WEBSOCKET
	useEffect(() => {
		// Only initialize if connection is null
		if (connection !== null) {
			return
		}

		const {
			bitcoin: { websocket },
		} = mempoolJS()

		const ws = websocket.initClient({
			options: [
				'blocks',
				'stats',
				'mempool-blocks',
				'live-2h-chart',
			],
		})

		// Store in ref so we can access it in cleanup
		wsRef.current = ws

		const onWSOpen = () => {
			console.info('[mempool ws] OPEN')
			setIsConnected(true)
			ws.send(JSON.stringify({ action: 'init' }))

			// Clear any pending reconnection timeout on successful connection
			if (reconnectTimeoutRef.current) {
				clearTimeout(reconnectTimeoutRef.current)
				reconnectTimeoutRef.current = null
				console.info('[mempool ws] cleared scheduled reconnection on successful open')
			}
		}

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const onWSClose = (event: any) => {
			console.error('[mempool ws] CLOSED', event)
			setIsConnected(false)
			setConnection({ ...ws, readyState: 3 } as WebSocket)

			// Don't reconnect if this was an intentional close (unmount/cleanup)
			if (intentionalCloseRef.current) {
				console.info('[mempool ws] intentional close, not reconnecting')
				intentionalCloseRef.current = false
				return
			}

			// Only attempt reconnection if page is visible and network is online
			const isPageVisible = !document.hidden
			const isNetworkOnline = navigator.onLine

			if (isPageVisible && isNetworkOnline) {
				console.info('[mempool ws] scheduling reconnection in 3s...')
				// Clear any existing timeout first
				if (reconnectTimeoutRef.current) {
					clearTimeout(reconnectTimeoutRef.current)
				}
				reconnectTimeoutRef.current = setTimeout(() => {
					console.info('[mempool ws] executing scheduled reconnection')
					setReconnecting(true)
					setConnection(null)
					reconnectTimeoutRef.current = null
				}, 3000)
			} else {
				console.info(`[mempool ws] skipping reconnection - page visible: ${isPageVisible}, network online: ${isNetworkOnline}`)
			}
		}

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const onWSMessage = function incoming({ data }: any) {
			const res = JSON.parse(data.toString())
			const {
				blocks,
				block,
				vBytesPerSecond,
				da,
				fees,
				mempoolInfo,
			} = res
			const mempoolBlocks = res['mempool-blocks']

			if (blocks) {
				handleInitializeBlocks(blocks)
			}
			if (block) {
				handleSetBlock(block)
			}
			if (da) {
				handleSetDa(da)
			}
			if (mempoolInfo) {
				handleSetMempoolInfo(mempoolInfo)
			}
			if (fees) {
				handleSetFees(fees)
			}
			if (vBytesPerSecond && vBytesPerSecond !== undefined) {
				setInflow(vBytesPerSecond)
			}
			if (mempoolBlocks && mempoolBlocks.length) {
				handleSetMempoolBlocks(mempoolBlocks)
			}
		}

		const reconnectString = reconnecting ? '(reconnecting...)' : ''
		console.info(`[mempool ws] initialize ${reconnectString}`)
		ws.addEventListener('open', onWSOpen)
		ws.addEventListener('message', onWSMessage)
		ws.addEventListener('close', onWSClose)

		setConnection(ws)
		if (reconnecting) {
			setReconnecting(false)
		}

		// Don't return cleanup here - let the separate effect handle it
	}, [connection, reconnecting])

	// Separate cleanup effect for websocket
	useEffect(() => {
		return () => {
			console.info('[mempool ws] cleanup on unmount')
			// Mark this as intentional close so reconnection doesn't trigger
			intentionalCloseRef.current = true
			// Clear any pending reconnection timeout
			if (reconnectTimeoutRef.current) {
				clearTimeout(reconnectTimeoutRef.current)
				reconnectTimeoutRef.current = null
			}
			// Close websocket on unmount
			if (wsRef.current) {
				wsRef.current.close()
				wsRef.current = null
			}
		}
	}, [])

	// PAGE VISIBILITY AND NETWORK STATUS HANDLERS
	useEffect(() => {
		const handleVisibilityChange = () => {
			const isVisible = !document.hidden
			console.info(`[mempool ws] page ${isVisible ? 'visible' : 'hidden'}`)
			setPageVisible(isVisible)

			if (isVisible && networkOnline) {
				// When page becomes visible, check connection and reconnect if needed
				if (!connection || connection.readyState === WebSocket.CLOSED) {
					// Clear any pending reconnection timeout and reconnect immediately
					if (reconnectTimeoutRef.current) {
						console.info('[mempool ws] canceling scheduled reconnection, reconnecting immediately')
						clearTimeout(reconnectTimeoutRef.current)
						reconnectTimeoutRef.current = null
					} else {
						console.info('[mempool ws] page visible - reconnecting immediately')
					}
					setReconnecting(true)
					setConnection(null)

					// Refresh 24-hour blocks data when coming back from being hidden
					console.info('[24h blocks] refreshing after page visible')
					handleGetPastBlocks()
				}
			} else if (!isVisible) {
				// When page becomes hidden, cancel any pending reconnection
				if (reconnectTimeoutRef.current) {
					console.info('[mempool ws] page hidden - canceling scheduled reconnection')
					clearTimeout(reconnectTimeoutRef.current)
					reconnectTimeoutRef.current = null
				}
			}
		}

		const handleOnline = () => {
			console.info('[mempool ws] network online')
			setNetworkOnline(true)

			// Reconnect when network comes back online
			if (pageVisible) {
				if (!connection || connection.readyState === WebSocket.CLOSED) {
					// Clear any pending reconnection timeout and reconnect immediately
					if (reconnectTimeoutRef.current) {
						console.info('[mempool ws] canceling scheduled reconnection, reconnecting immediately')
						clearTimeout(reconnectTimeoutRef.current)
						reconnectTimeoutRef.current = null
					} else {
						console.info('[mempool ws] network restored - reconnecting immediately')
					}
					setReconnecting(true)
					setConnection(null)

					// Refresh 24-hour blocks data when network comes back
					console.info('[24h blocks] refreshing after network restored')
					handleGetPastBlocks()
				}
			}
		}

		const handleOffline = () => {
			console.info('[mempool ws] network offline')
			setNetworkOnline(false)

			// Clear any pending reconnection timeout when going offline
			if (reconnectTimeoutRef.current) {
				console.info('[mempool ws] network offline - canceling scheduled reconnection')
				clearTimeout(reconnectTimeoutRef.current)
				reconnectTimeoutRef.current = null
			}
		}

		// Set initial state
		setPageVisible(!document.hidden)
		setNetworkOnline(navigator.onLine)

		// Add event listeners
		document.addEventListener('visibilitychange', handleVisibilityChange)
		window.addEventListener('online', handleOnline)
		window.addEventListener('offline', handleOffline)

		return () => {
			// Cleanup event listeners
			document.removeEventListener('visibilitychange', handleVisibilityChange)
			window.removeEventListener('online', handleOnline)
			window.removeEventListener('offline', handleOffline)
		}
	}, [connection, networkOnline, pageVisible])

	return null
}
